import { CONNECTOR_STATUS_LABELS, formatStamp } from '@baseline/shared';

import { useConnectors } from '../api/queries.js';
import { PageError } from '../components/PageStatus.js';

import type { Connector } from '@baseline/shared';

// Prototype `vConn` (plan Phase 11) on `GET /connectors` (spec §5.8).

const PILL = { connected: 'p-ok', failed: 'p-new', not_configured: 'p-accepted' } as const;

function lastRead(c: Connector): string {
  if (!c.lastSuccessfulReadAt) return c.status === 'connected' ? 'Ready' : 'Not read yet';
  return `Last read on ${formatStamp(c.lastSuccessfulReadAt)}`;
}

/** Spec §5.8: a read that found the file unchanged says since when. */
function unchanged(c: Connector): string | undefined {
  return c.unchangedSince ? `Unchanged since ${formatStamp(c.unchangedSince)}` : undefined;
}

export default function ConnectorsPage() {
  const { data, error } = useConnectors();
  if (error) return <PageError error={error} />;
  return (
    <div className="pg">
      <div className="ph">
        <div>
          <h1>Connectors</h1>
          <p className="ps">Where Baseline reads your data.</p>
        </div>
      </div>
      <div className="cgrid">
        {(data?.connectors ?? []).map((c) => (
          <div key={c.id} className="ccard">
            <div className="cn2">
              <b>{c.name}</b>
              <span>{c.description}</span>
              {c.location ? <span className="mono-s">{c.location}</span> : null}
            </div>
            <div className="cmeta">
              <span className={`pill ${PILL[c.status]}`}>{CONNECTOR_STATUS_LABELS[c.status]}</span>
              <span className="cw2" title={unchanged(c)}>
                {lastRead(c)}
              </span>
            </div>
            {c.lastErrorDetail?.length ? (
              <ul className="err">
                {c.lastErrorDetail.slice(0, 8).map((e, i) => (
                  <li key={i}>
                    Row {e.row}: {e.msg}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
      <p className="hint" style={{ marginTop: 14 }}>
        Connectors are set up when Baseline is installed. Baseline only reads them; it never writes
        to PDM, ERP or change orders.
      </p>
    </div>
  );
}
